import { ReactNode } from 'react';
import { BoxStyleProps } from '../Box';
import IconOverlap from '../IconOverlap/IconOverlap';
import Image from '../Image/Image';
import { OptionItemInfoText } from '../OptionItem';
import OptionItem from '../OptionItem/OptionItem';
import Text from '../Text/Text';

import styles from './CurrencyOption.module.css';

export interface CurrencyOptionProps extends BoxStyleProps {
  className?: string;
  currencyName: string;
  currencySymbol: string;
  currencyAmount?: string;
  networkName?: string;
  fiatAmount?: string;
  fiatSymbol?: string;
  supportedNetworks?: SupportedNetwork[];
  loading?: boolean;
  disabled?: boolean;
  currencyIconSrc?: string;
  networkIconSrc?: string;
  customRightTopSlot?: ReactNode;
  customRightBottomSlot?: ReactNode;
  infoText?: string;
  showInfoTooltipOnHover?: boolean;
  onClick?: () => void;
}

export default function CurrencyOption({
  className,
  currencyName,
  currencySymbol,
  currencyAmount,
  networkName,
  fiatAmount,
  fiatSymbol,
  currencyIconSrc,
  networkIconSrc,
  supportedNetworks,
  customRightBottomSlot,
  customRightTopSlot,
  disabled,
  infoText,
  showInfoTooltipOnHover,
  ...otherProps
}: CurrencyOptionProps): JSX.Element {
  const name = networkName ? currencyName : currencySymbol;
  const description = networkName ? networkName : currencyName;

  const rightTopSlot =
    customRightTopSlot ??
    (networkName ? (
      <Text
        size="small"
        weight="600"
        color={disabled ? 'neutral-400' : 'neutral-900'}
      >
        {currencyAmount} {currencySymbol}
      </Text>
    ) : (
      supportedNetworks && (
        <SupportedNetworksIconGroup supportedNetworks={supportedNetworks} />
      )
    ));

  const rightBottomSlot = customRightBottomSlot ?? (
    <Text size="tiny" weight="400" color="neutral-500">
      {networkName ? `${fiatAmount} ${fiatSymbol}` : 'Networks'}
    </Text>
  );

  const icon = networkName ? (
    <IconOverlap
      size={32}
      mainIcon={<Image src={currencyIconSrc} alt={currencyName} />}
      overlapIcon={<Image src={networkIconSrc} alt={networkName} />}
    />
  ) : (
    <div className={styles['icon-wrapper']}>
      <Image src={currencyIconSrc} alt={currencyName} />
    </div>
  );

  return (
    <OptionItem
      {...otherProps}
      name={name}
      description={description}
      icon={icon}
      disabled={disabled}
      rightTopSlot={rightTopSlot}
      rightBottomSlot={
        infoText ? <OptionItemInfoText text={infoText} /> : rightBottomSlot
      }
      showInfoTooltipOnHover={showInfoTooltipOnHover}
    />
  );
}

interface SupportedNetwork {
  iconSrc: string;
  alt: string;
}

function SupportedNetworksIconGroup({
  supportedNetworks,
}: {
  supportedNetworks: SupportedNetwork[];
}) {
  return (
    <div className={styles['supported-networks']}>
      {supportedNetworks.map((network) => (
        <div key={network.iconSrc} className={styles['icon-wrapper']}>
          <Image
            data-test-id="supported-network-icon"
            width={16}
            height={16}
            src={network.iconSrc}
            alt={network.alt}
            className={styles['icon']}
          />
        </div>
      ))}
    </div>
  );
}
